import { useState, useEffect } from 'react';
import { Handshake, Search, Filter, Plus, Eye, Pencil, Trash2, Calendar, Users, Building2, Check, X, Link as LinkIcon, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { Dialog, Listbox } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { Collab, getDocuments, deleteDocument, updateDocument } from '../lib/supabaseHelpers';
import { useAuth } from '../contexts/AuthContextSupabase';
import InfluencerAvatar from '../components/InfluencerAvatar';

const COLLAB_TYPES = [
  { value: 'collaboration_entrante', label: 'Collaboration entrante' },
  { value: 'collaboration_sortante', label: 'Collaboration sortante' },
  { value: 'casting_interne', label: 'Casting interne' },
  { value: 'casting_externe', label: 'Casting externe' },
] as const;

export default function Collaborations() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [collabs, setCollabs] = useState<Collab[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCollab, setSelectedCollab] = useState<Collab | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [copiedLink, setCopiedLink] = useState<string | null>(null);

  const sortCollabsByDate = (collabs: Collab[]) => {
    return [...collabs].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA;
    });
  };

  useEffect(() => {
    const loadData = async () => {
      console.log('Collaborations - loadData called', {
        currentUser: currentUser?.uid,
        agencyId: userProfile?.agency_id
      });

      if (!currentUser || !userProfile?.agency_id) {
        console.log('Collaborations - No user or agency_id, stopping load');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        console.log('Collaborations - Fetching collabs for agency:', userProfile.agency_id);
        const collabsData = await getDocuments<Collab>('collabs', userProfile.agency_id);
        console.log('Collaborations - Received collabs:', collabsData.length);
        setCollabs(sortCollabsByDate(collabsData));
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [userProfile?.agency_id]);

  const handleDelete = async () => {
    try {
      if (!selectedCollab?.id) return;
      console.log(`Tentative de suppression de la collaboration: ${selectedCollab.id} (${selectedCollab.name}) - ${new Date().toISOString()}`);
      
      // Mettre à jour l'état local immédiatement pour une meilleure UX
      setCollabs(prevCollabs => {
        console.log(`Filtrage des collaborations: ${prevCollabs.length} avant suppression locale`);
        const filtered = prevCollabs.filter(collab => collab.id !== selectedCollab.id);
        console.log(`${filtered.length} collaborations après suppression locale`);
        return filtered;
      });
      
      // Fermer le modal de confirmation
      setIsDeleteModalOpen(false);
      setSelectedCollab(null);
      
      // Supprimer la collaboration de Supabase
      try {
        const result = await deleteDocument('collabs', selectedCollab.id);
        if (result.success) {
          console.log(`Collaboration ${selectedCollab.id} supprimée avec succès`);
        }
      } catch (deleteError) {
        console.error('Erreur lors de la suppression de la collaboration:', deleteError);
      }
    } catch (error) {
      console.error('Erreur générale lors de la suppression:', error);
    }
  };

  const handleViewDetails = (collab: Collab) => {
    navigate(`/app/collaborations/${collab.id}`);
  };

  const handleEdit = (collab: Collab, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/app/collaborations/${collab.id}`);
  };

  const copyPublicLink = async (collab: Collab, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!collab.id || !collab.linkToken) {
      try {
        const linkToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        
        // Mettre à jour la collaboration avec le token et activer le lien
        await updateDocument('collabs', collab.id!, {
          link_token: linkToken,
          link_active: true
        });
        
        // Mettre à jour l'état local
        setCollabs(prevCollabs =>
          prevCollabs.map(c =>
            c.id === collab.id
              ? { ...c, link_token: linkToken, link_active: true }
              : c
          )
        );
        
        const publicUrl = `${window.location.origin}/suivi/${linkToken}`;
        await navigator.clipboard.writeText(publicUrl);
        setCopiedLink(collab.id);
        setTimeout(() => setCopiedLink(null), 3000);
      } catch (error) {
        console.error('Error generating link:', error);
      }
      return;
    }
    
    try {
      const publicUrl = `${window.location.origin}/suivi/${collab.link_token}`;
      await navigator.clipboard.writeText(publicUrl);
      setCopiedLink(collab.id);
      setTimeout(() => setCopiedLink(null), 3000);
    } catch (error) {
      console.error('Error copying link:', error);
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return 'Non définie';

    try {
      return new Date(date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', date, error);
      return 'Date invalide';
    }
  };

  const getStatusBadgeClass = (status: string) => {
    if (status === 'completed') return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/20';
    if (status === 'in_progress') return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/20';
    return 'bg-blue-500/20 text-blue-400 border-blue-500/20';
  };

  const getStatusText = (status: string) => {
    if (status === 'completed') return 'Terminée';
    if (status === 'in_progress') return 'En cours';
    return 'En attente';
  };

  const handleStatusClick = async (collab: Collab, e: React.MouseEvent) => {
    e.stopPropagation();

    const currentStatus = collab.status || 'pending';
    let newStatus: string;

    if (currentStatus === 'pending') {
      newStatus = 'in_progress';
    } else if (currentStatus === 'in_progress') {
      newStatus = 'completed';
    } else {
      newStatus = 'pending';
    }

    try {
      await updateDocument('collabs', collab.id!, { status: newStatus });

      setCollabs(prevCollabs =>
        prevCollabs.map(c =>
          c.id === collab.id ? { ...c, status: newStatus } : c
        )
      );
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const filteredCollabs = collabs.filter(collab => {
    const matchesSearch =
      collab.name.toLowerCase().includes(search.toLowerCase()) ||
      collab.brand.toLowerCase().includes(search.toLowerCase());

    const status = collab.status || 'pending';
    const matchesStatus = statusFilter === 'all' || status === statusFilter;

    const matchesType = typeFilter === 'all' || collab.type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  // Pas besoin de trier à nouveau ici car les collaborations sont déjà triées dans loadData

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white/10 border-t-white/60"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-accent-blue/10 flex items-center justify-center">
            <Handshake className="w-6 h-6 text-accent-blue" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">
              Collaborations
            </h1>
            <p className="text-white/50">
              Gérez vos collaborations avec les influenceurs
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/app/collaborations/new')}
          className="flex items-center gap-2 px-4 py-2 bg-white text-black font-semibold rounded-xl hover:bg-white/90"
        >
          <Plus className="w-5 h-5" />
          Nouvelle collaboration
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white/[0.04] border-white/[0.08] rounded-xl border p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par nom ou marque..."
                className="w-full pl-10 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Listbox value={statusFilter} onChange={setStatusFilter}>
              <div className="relative min-w-[150px]">
                <Listbox.Button className="relative w-full px-4 py-2 bg-white/[0.06] border border-white/[0.08] rounded-xl text-left text-white focus:outline-none focus:ring-1 focus:ring-white/20 cursor-pointer">
                  <span className="block truncate pr-8 text-white/60">
                    {statusFilter === 'all' ? 'Tous les statuts' : statusFilter === 'pending' ? 'En attente' : statusFilter === 'in_progress' ? 'En cours' : 'Terminées'}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="h-5 w-5 text-white/30" />
                  </span>
                </Listbox.Button>
                <Listbox.Options className="absolute z-50 mt-1 w-full bg-[#141414] border border-white/[0.08] rounded-xl shadow-lg max-h-60 overflow-auto focus:outline-none">
                  <Listbox.Option value="all" className={({ active }) => `cursor-pointer select-none relative py-2 px-3 ${active ? 'bg-white/[0.08] text-white' : 'text-white/60'}`}>
                    {({ selected }) => <span className={selected ? 'font-medium' : 'font-normal'}>Tous les statuts</span>}
                  </Listbox.Option>
                  <Listbox.Option value="pending" className={({ active }) => `cursor-pointer select-none relative py-2 px-3 ${active ? 'bg-white/[0.08] text-white' : 'text-white/60'}`}>
                    {({ selected }) => <span className={selected ? 'font-medium' : 'font-normal'}>En attente</span>}
                  </Listbox.Option>
                  <Listbox.Option value="in_progress" className={({ active }) => `cursor-pointer select-none relative py-2 px-3 ${active ? 'bg-white/[0.08] text-white' : 'text-white/60'}`}>
                    {({ selected }) => <span className={selected ? 'font-medium' : 'font-normal'}>En cours</span>}
                  </Listbox.Option>
                  <Listbox.Option value="completed" className={({ active }) => `cursor-pointer select-none relative py-2 px-3 ${active ? 'bg-white/[0.08] text-white' : 'text-white/60'}`}>
                    {({ selected }) => <span className={selected ? 'font-medium' : 'font-normal'}>Terminées</span>}
                  </Listbox.Option>
                </Listbox.Options>
              </div>
            </Listbox>
            <Listbox value={typeFilter} onChange={setTypeFilter}>
              <div className="relative min-w-[180px]">
                <Listbox.Button className="relative w-full px-4 py-2 bg-white/[0.06] border border-white/[0.08] rounded-xl text-left text-white focus:outline-none focus:ring-1 focus:ring-white/20 cursor-pointer">
                  <span className="block truncate pr-8 text-white/60">
                    {typeFilter === 'all' ? 'Tous les types' : COLLAB_TYPES.find(t => t.value === typeFilter)?.label}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="h-5 w-5 text-white/30" />
                  </span>
                </Listbox.Button>
                <Listbox.Options className="absolute z-50 mt-1 w-full bg-[#141414] border border-white/[0.08] rounded-xl shadow-lg max-h-60 overflow-auto focus:outline-none">
                  <Listbox.Option value="all" className={({ active }) => `cursor-pointer select-none relative py-2 px-3 ${active ? 'bg-white/[0.08] text-white' : 'text-white/60'}`}>
                    {({ selected }) => <span className={selected ? 'font-medium' : 'font-normal'}>Tous les types</span>}
                  </Listbox.Option>
                  {COLLAB_TYPES.map((type) => (
                    <Listbox.Option key={type.value} value={type.value} className={({ active }) => `cursor-pointer select-none relative py-2 px-3 ${active ? 'bg-white/[0.08] text-white' : 'text-white/60'}`}>
                      {({ selected }) => <span className={selected ? 'font-medium' : 'font-normal'}>{type.label}</span>}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            </Listbox>
          </div>
        </div>
      </div>

      {/* Collaborations Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredCollabs.map((collab) => (
          <div
            key={collab.id}
            onClick={() => handleViewDetails(collab)}
            className="bg-white/[0.04] border-white/[0.08] hover:bg-white/[0.06] rounded-xl border p-6 cursor-pointer transition-colors"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="font-semibold text-white mb-1">
                  {collab.name}
                </h3>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-white/30" />
                  <span className="text-sm text-white/50">
                    {collab.brand}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => copyPublicLink(collab, e)}
                  className="p-2 rounded-lg hover:bg-white/[0.08] transition-colors"
                  title="Copier le lien public"
                >
                  {copiedLink === collab.id ? (
                    <Check className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <LinkIcon className="w-4 h-4 text-white/30" />
                  )}
                </button>
                <button
                  onClick={(e) => handleEdit(collab, e)}
                  className="p-2 rounded-lg hover:bg-white/[0.08] transition-colors"
                >
                  <Pencil className="w-4 h-4 text-white/30" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCollab(collab);
                    setIsDeleteModalOpen(true);
                  }}
                  className="p-2 rounded-lg hover:bg-white/[0.08] transition-colors"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/50">
                  Statut
                </span>
                <button
                  onClick={(e) => handleStatusClick(collab, e)}
                  className={`px-2 py-1 rounded-full text-xs border transition-all hover:scale-105 hover:shadow-sm flex items-center gap-1 ${getStatusBadgeClass(collab.status || 'pending')}`}
                  title="Cliquer pour changer le statut"
                >
                  {getStatusText(collab.status || 'pending')}
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-white/50">
                  Type
                </span>
                <span className="text-sm text-white/60">
                  {COLLAB_TYPES.find(type => type.value === collab.type)?.label}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-white/50">
                  Influenceurs
                </span>
                <div className="flex items-center gap-1">
                  {collab.influencers && collab.influencers.length > 0 ? (
                    <div className="flex -space-x-2">
                      {collab.influencers.slice(0, 3).map((influencer: any, idx: number) => (
                        <div
                          key={idx}
                          className="relative"
                          title={influencer.name || 'Influenceur'}
                        >
                          <InfluencerAvatar
                            influencer={influencer}
                            size="sm"
                            className="ring-2 ring-white/10"
                          />
                        </div>
                      ))}
                      {collab.influencers.length > 3 && (
                        <div className="w-8 h-8 rounded-full bg-white/[0.08] flex items-center justify-center text-xs font-medium text-white/60 ring-2 ring-white/10">
                          +{collab.influencers.length - 3}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4 text-white/30" />
                      <span className="text-sm text-white/60">0</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-white/50">
                  Budget
                </span>
                <span className="text-sm font-medium text-white">
                  {collab.budget ? `${collab.budget}€` : 'Non défini'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-white/50">
                  Date de signature
                </span>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-white/30" />
                  <span className="text-sm text-white/60">
                    {formatDate(collab.signed_date)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredCollabs.length === 0 && (
        <div className="text-center py-12 text-white/30">
          <div className="w-16 h-16 rounded-xl bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
            <Handshake className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-medium mb-2 text-white/30">Aucune collaboration trouvée</h3>
          <p className="mb-4">
            {search || statusFilter !== 'all' || typeFilter !== 'all'
              ? 'Aucune collaboration ne correspond à vos critères de recherche.'
              : 'Commencez par créer votre première collaboration.'}
          </p>
          {(!search && statusFilter === 'all' && typeFilter === 'all') && (
            <button
              onClick={() => navigate('/app/collaborations/new')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black font-semibold rounded-xl hover:bg-white/90"
            >
              <Plus className="w-4 h-4" />
              Créer une collaboration
            </button>
          )}
        </div>
      )}

      {/* Delete Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white">
                  Supprimer la collaboration
                </h3>
                <p className="text-sm text-white/50">
                  Cette action est irréversible
                </p>
              </div>
            </div>

            <p className="mb-6 text-white/60">
              Êtes-vous sûr de vouloir supprimer la collaboration "{selectedCollab?.name}" ?
              Toutes les données associées seront définitivement perdues.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08] rounded-xl transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}